import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './s.module.less';
import {
    EMessageSenderType,
    EWSAction,
    TConversation,
    TConversationMember,
    TServerMessage,
    TWSMessage,
    EConversationType,
} from 'types/inbox';
import { ClockCircleOutlined } from '@ant-design/icons';
import { getTimeStr } from 'utils/calendar';
import dayjs from 'dayjs';
import OpsInboxMessageList from './components/OpsInboxMessageList';
import IntakeFormReminderButton from './components/IntakeFormReminderButton';
import AppointmentReminderButton from './components/AppointmentReminderButton';
import { EAIEditorType } from 'types/ai';
import ProsepectInfo from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo';
import { handleServerSideMessage, unitAndSortServerMessages, getInfoFromConversation } from 'utils/inbox';
import EmptyIcon from './imgs/empty.svg';
import { Button, Input, message, Modal, Popover, Select, Spin, Tooltip } from 'antd';
import { TOpsClient } from 'types/common';
import { getUserId } from 'utils/localstore';
import useOpsMessageConversations from 'hooks/opsMessage/useOpsMessageConversations';
import useOpsMessageAllUnreadCount from 'hooks/opsMessage/useOpsMessageAllUnreadCount';
import { TWebSocketListener, useOpsMessageWebSocket } from 'hooks/opsMessage/useOpsMessageWebSocket';
import { VALID_DOMAIN } from 'constants/common';
import { getUrlParam } from 'utils/common';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import ChatInput, { ChatInputValue } from 'components/ChatInput';
import TextVisitUnSubmittedDrawer from 'components/TextVisitUnSubmittedDrawer';
import { CaseListItem } from 'types/textVisit';
import useOpsUserList from 'hooks/useOpsUserList';
import {
    opsMessageTransfer,
    opsMessageClose,
    opsMarkReadForConversation,
    opsGetConversationMessageList,
    opsSendMessage,
    TOpsGetConversationMessageListV2,
} from 'api/operation';
import useOpsPatientInfo from 'hooks/useOpsPatientInfo';
import useOpsPatient from 'hooks/useOpsPatient';

type Props = {
    clickSendMsgFlag?: {};
};

const DEFAULT_FETCH_MESSAGE_COUNT = 30;
const DEFAULT_FETCH_CONVERSATION_COUNT = 50;
const LOAD_HISTORY_INTERVAL = 1000 * 2;

const OpsMessageContainer = (props: Props) => {
    const { clickSendMsgFlag } = props;
    const navigate = useNavigate();
    const searchParams = () => new URLSearchParams(location.search);
    const client_id = searchParams().get('client_id');
    // const [get, set] = InboxStore.useStore();

    const conversationMessageMap = React.useRef<Record<string, TServerMessage[] | undefined>>({});
    const allConversationMessageFetchedFlag = React.useRef<Record<string, boolean>>({});
    const latestConversationMessageCursor = React.useRef<Record<string, string>>({});
    const latestLoadHistoryTime = React.useRef<number>(new Date().getTime());
    const [hasFocusOnDefaultConversation, setHasFocusOnDefaultConversation] = useState<boolean>(false);
    const [allClient, loading, run] = useOpsPatient();

    const [selectConversationId, setSelectConversationId] = useState<string>();
    const selectConversationIdRef = React.useRef<string | undefined>(selectConversationId);

    const fetchConversationSize = useRef<number>(DEFAULT_FETCH_CONVERSATION_COUNT);

    const urlClientId = useRef<string | undefined>(getUrlParam('clientId') || undefined);

    const [conversation, conversationLoading, refetchConversation] = useOpsMessageConversations(
        fetchConversationSize.current,
    );
    const [allUnreadCount, unreadLoading, refetchAllUnreadCount] = useOpsMessageAllUnreadCount();

    const [showUnreadConversationOnly, setShowUnreadConversationOnly] = useState<boolean>(false);
    const [loadingConversationMessage, setLoadingConversationMessage] = useState<boolean>(false);

    const [showClientInfo, setShowClientInfo] = useState<boolean>(true);
    const loadingHistoryMessageRef = useRef<boolean>(false);
    const [, forceUpdate] = useState<{}>();

    const [messageListUnreadCount, setMessageListUnreadCount] = useState<number>(0);

    const autoFetchConversationInterval = React.useRef<NodeJS.Timer>();

    const hasMoreConversation = !!(conversation && conversation.length === fetchConversationSize.current);

    const targetConversation = conversation?.find((item) => item.conversationId === selectConversationId);
    const conversationId = selectConversationId || selectConversationIdRef.current;
    const [forceResetInfo, setForceResetInfo] = useState<{}>({});

    const providerId = getUserId();

    const [homeInfo, loadingHomeInfo, refetchHomeInfo] = useProviderHomeInfo();
    const isPPAProvider = homeInfo?.ppaProvider;
    // const [billingPolicy, loadingBillingPolicy] = useBillingPolicy(true, providerId);

    const [showTextVisitUnSubmittedDrawer, setShowTextVisitUnSubmittedDrawer] = useState(false);
    const [selectedCase, setSelectedCase] = useState<CaseListItem>();
    const [showPreviewDrawer, setShowPreviewDrawer] = useState(false);
    const [nextCaseUuid, setNextCaseUuid] = useState<string>();
    const targetConversationInfo = getInfoFromConversation(targetConversation);
    const [messageList, setMessageList] = useState<TServerMessage[]>();
    const [currentTab, setCurrentTab] = useState<string>('assigned_to_me');
    const [conversationSearchValue, setConversationSearchValue] = useState<string>('');
    const [showCloseChatConfirmModal, setShowCloseChatConfirmModal] = useState<boolean>(false);
    const [closingChat, setClosingChat] = useState<boolean>(false);
    const [showTransferChatConfirmModal, setShowTransferChatConfirmModal] = useState<boolean>(false);
    const [transferChatTo, setTransferChatTo] = useState<number>();
    const [transferNote, setTransferNote] = useState<string>();
    const [transferringChat, setTransferringChat] = useState<boolean>(false);
    const [opsUserList, loadingOpsUserList, refetchOpsUserList] = useOpsUserList();
    const conversationAssignToMe = conversation?.filter((item) => {
        const members = item.members || [];
        return members.some((member) => member.userId === getUserId());
    });

    let patientId: number | undefined;
    if (targetConversationInfo) {
        patientId = targetConversationInfo.patientId;
    }

    const [targetClientDetail, loadingTargetClientDetail, runTargetClientDetail] = useOpsPatientInfo(
        patientId && showClientInfo ? patientId : undefined,
    );

    const resetAutoFetchInterval = () => {
        if (autoFetchConversationInterval.current) {
            clearInterval(autoFetchConversationInterval.current);
        }
        autoFetchConversationInterval.current = setInterval(() => {
            refetchConversation(fetchConversationSize.current);
            refetchAllUnreadCount();
        }, 1000 * 8); // 5 s
    };

    const scrollToLatestMessage = (timeout?: number, smooth?: boolean) => {
        setTimeout(() => {
            const messageWrap = document.getElementById('messageWrap');
            if (messageWrap) {
                messageWrap.scrollTo({
                    top: 99999,
                    behavior: smooth ? 'smooth' : 'auto',
                });
            }
        }, timeout || 150);
    };

    const handleCloseChat = async () => {
        if (!conversationId) {
            message.error('Please select a conversation');
            return;
        }
        setClosingChat(true);

        const res = await opsMessageClose(conversationId);
        if (!res.error) {
            message.success('Conversation closed successfully');
            refetchConversation(fetchConversationSize.current);
            refetchAllUnreadCount();
            setSelectConversationId(undefined);
            selectConversationIdRef.current = undefined;
            setClosingChat(false);
            setShowCloseChatConfirmModal(false);
            run();
        }
    };

    const handleTransferChat = async () => {
        if (!transferChatTo) {
            message.error('Please select an ops team member');
            return;
        }
        if (!conversationId) {
            message.error('Please select a conversation');
            return;
        }
        setTransferringChat(true);
        const res = await opsMessageTransfer({
            newAssignedOpsId: transferChatTo,
            note: transferNote,
            conversationId: conversationId!,
        });
        if (!res.error) {
            message.success('Conversation transferred successfully');
            refetchConversation(fetchConversationSize.current);
            refetchAllUnreadCount();
            setSelectConversationId(undefined);
            selectConversationIdRef.current = undefined;
            setTransferringChat(false);
            setShowTransferChatConfirmModal(false);
            run();
        }
    };

    const socketListeners = React.useRef<TWebSocketListener>({
        onOpen: (event) => {
            console.log('WebSocket connection opened:', event);
            // Optionally, you can send a message or perform an action when the connection opens
            // socketRef.current.send(JSON.stringify({ type: 'subscribe', channel: 'inbox' }));
            resetAutoFetchInterval();
            refetchConversation(fetchConversationSize.current);
            refetchAllUnreadCount();
        },
        onClose: (event) => {
            console.warn('WebSocket connection closed:', event);
            // Optionally, you can try to reconnect here
            // socketRef.current = new WebSocket(WEBSOCKET_URL);
        },
        onMessage: (event) => {
            console.info('WebSocket onMessage:', event);
            const msgData = event.data;
            try {
                const wsMsg: TWSMessage = JSON.parse(msgData);
                if (wsMsg.action === EWSAction.NEWMSG) {
                    const serverMsg = wsMsg.payload as TServerMessage;
                    const conversationIdFromWs = serverMsg.conversationId;
                    const cacheMessageList = conversationMessageMap.current[conversationIdFromWs] || [];
                    let _messageObj = unitAndSortServerMessages([...(cacheMessageList || []), serverMsg]);

                    const currentSortId = serverMsg.sortKey;
                    const targetIndex = cacheMessageList.findIndex((item) => item.sortKey === currentSortId);
                    if (targetIndex >= 0) {
                        cacheMessageList[targetIndex] = { ...serverMsg };
                        _messageObj = unitAndSortServerMessages(cacheMessageList);
                    }

                    conversationMessageMap.current[conversationIdFromWs] = _messageObj || [];
                    // console.log({
                    //     _message,
                    // })
                    if (selectConversationIdRef.current === conversationIdFromWs) {
                        setMessageList(_messageObj || []);
                        scrollToLatestMessage(undefined, true);
                    }
                } else {
                    console.warn('Unknown WebSocket action:', msgData);
                }
            } catch (e) {
                console.error(e);
            }
        },
        onError: (event) => {
            console.error('WebSocket error:', event);
        },
        autoCloseTime: 1000 * 60 * 10, // 10 minutes
        autoReconnectTime: 1000 * 20, // 20 seconds
    });

    const [socketRef] = useOpsMessageWebSocket(socketListeners.current);
    const [replayContent, setReplayContent] = useState<string>('');
    const [replayContentValue, setReplayContentValue] = useState<ChatInputValue>({
        text: replayContent,
        files: [],
    });
    const [resetFlag, setResetFlag] = useState<{}>({});
    const [clearMessageFlag, setClearMessageFlag] = useState<{}>();

    const [newMsgClientId, setNewMsgClientId] = useState<number>();
    const [newMsgContent, setNewMsgContent] = useState<ChatInputValue>({ text: '', files: [] });

    const [showNewMsgModal, setShowNewMsgModal] = useState<boolean>(false);
    const [sendingNewMsg, setSendingNewMsg] = useState<boolean>(false);

    const [replyingMsg, setReplyingMsg] = useState<boolean>(false); //main pannel

    const inputWidth = window.document.body.clientWidth - (showClientInfo ? 250 : 0) - 320 - 255;

    const isTargetProviderReview =
        targetConversation &&
        (targetConversation.conversationType === EConversationType.PROVIDER_REVIEW ||
            targetConversation.conversationType === 'PROVIDER_REVIEW');

    let conversationLabelText = '';
    if (isTargetProviderReview) {
        conversationLabelText = 'KR'; // Klarity Reviews 的缩写
    } else {
        if (targetConversationInfo && targetConversationInfo.patientFirstName) {
            conversationLabelText += targetConversationInfo.patientFirstName.substring(0, 1);
        }
        if (targetConversationInfo && targetConversationInfo.patientLastName) {
            conversationLabelText += targetConversationInfo.patientLastName.substring(0, 1);
        }
    }
    const isEmpty = !conversation || conversation?.length === 0;
    const canEdit = targetConversation?.isOpsByMe && targetConversation.status === 'ACTIVE';

    const displayMessage = handleServerSideMessage(messageList, messageListUnreadCount);

    const getAssignedOpsLabel = React.useCallback((item?: Pick<TConversation, 'members'>) => {
        const members: TConversationMember[] = item?.members || [];
        const opsMembers = members.filter((m) => m.userType === EMessageSenderType.OPS);
        if (!opsMembers.length) return '';
        const names = opsMembers
            .map((m) => (m.fullName || `${m.firstName || ''} ${m.lastName || ''}`.trim()).trim())
            .filter(Boolean);
        return names.join(', ');
    }, []);

    const getAssignedOpsEmails = React.useCallback(
        (item?: Pick<TConversation, 'members'>) => {
            const members: TConversationMember[] = item?.members || [];
            const opsMembers = members.filter((m) => m.userType === EMessageSenderType.OPS);
            if (!opsMembers.length) return [];
            const emails = opsMembers
                .map((m) => opsUserList?.find((u) => `${u.id}` === `${m.userId}`)?.gmail)
                .filter((v): v is string => !!v && !!v.trim())
                .map((v) => v.trim());
            return Array.from(new Set(emails));
        },
        [opsUserList],
    );

    const displayConversation = conversation?.filter((item) => {
        if (item.conversationId === selectConversationId) {
            return true;
        }
        let markUnread = true;
        if (showUnreadConversationOnly) {
            markUnread = !!(item.unreadCount && item.unreadCount > 0);
        }
        let markSearch = true;
        if (conversationSearchValue) {
            const itemInfo = getInfoFromConversation(item);
            const name = `${itemInfo?.patientFirstName || ''} ${itemInfo?.patientLastName || ''}`.trim();
            markSearch = name.toLowerCase().includes(conversationSearchValue.toLowerCase());
        }
        let markTab = true;
        if (currentTab === 'assigned_to_me') {
            markTab = !!item?.members?.find((member) => {
                return `${member.userId}` === `${getUserId()}` && member.userType === EMessageSenderType.OPS;
            });
        }
        return markUnread && markSearch && markTab;
    });

    const hasUnreadMessageAll = !!(allUnreadCount && allUnreadCount > 0);

    React.useEffect(() => {
        if (patientId && showClientInfo) {
        }
    }, [patientId, showClientInfo]);

    const handleChangeConversation = async (cid: string) => {
        const cacheMessage = conversationMessageMap.current[cid] || [];
        setMessageList(cacheMessage);
        setSelectConversationId(cid);
        selectConversationIdRef.current = cid;

        const _targetConversation = conversation?.find((item) => item.conversationId === cid);
        if (_targetConversation) {
            setMessageListUnreadCount(_targetConversation.unreadCount || 0);
        }
        if (_targetConversation?.isOpsByMe) {
            const res = await opsMarkReadForConversation(cid);
            if (!res.error) {
                resetAutoFetchInterval();
                refetchConversation(fetchConversationSize.current);
                refetchAllUnreadCount();
            }
        }

        // setForceResetInfo({});
    };

    React.useEffect(() => {
        if (!client_id || !conversation || selectConversationId) return;

        let initConversationId = '';
        if (client_id) {
            initConversationId = conversation.find((item) => item.clientId === Number(client_id))?.conversationId ?? '';
            if (initConversationId) {
                handleChangeConversation(initConversationId);
            } else {
                setShowNewMsgModal(true);
                setNewMsgClientId(Number(client_id));
                setNewMsgContent({ text: '', files: [] });
                setResetFlag({});
            }
        }
    }, [conversation, client_id]);

    React.useEffect(() => {
        if (hasFocusOnDefaultConversation || !urlClientId.current) {
            return;
        }
        const targetCid = `${getUserId()}_${urlClientId.current}`;
        const targetConversationFromUrl = conversation?.find((item) => item.conversationId === targetCid);
        if (targetConversationFromUrl) {
            setHasFocusOnDefaultConversation(true);
            setSelectConversationId(targetCid);
            selectConversationIdRef.current = targetCid;
        }
    }, [conversation, hasFocusOnDefaultConversation]);

    const handleShowMsgModal = () => {
        setShowNewMsgModal(true);
        setNewMsgClientId(undefined);
        setNewMsgContent({ text: '', files: [] });
        setResetFlag({});
    };

    const checkContent = (content: string): boolean => {
        const regex = /https?:\/\/[^\s，,；;\]\)\(\[\“\"\'<>]+/g;
        const links = content.match(regex);
        let isValid = true;
        links?.forEach((link) => {
            try {
                const url = new URL(link);
                if (url.hostname) {
                    let hostIsValid = false;
                    VALID_DOMAIN.forEach((domain) => {
                        if (url.hostname.endsWith(domain)) {
                            hostIsValid = true;
                        }
                    });
                    if (!hostIsValid) {
                        isValid = false;
                    }
                }
            } catch (e) {
                console.error(e);
            }
        });
        return isValid;
    };

    const onClickSend = async () => {
        if (!newMsgClientId || (!newMsgContent.text.trim() && newMsgContent.files.length === 0) || !providerId) {
            message.error('Please select a client and enter a message.');
            return;
        }

        const msg = newMsgContent.text.trim() ? newMsgContent.text.replace(/<[^>]+>/g, '') : '';
        const files = newMsgContent.files.map((file) => file.id);
        const client = allClient?.find((item) => item.klarityUserId === newMsgClientId);

        setSendingNewMsg(true);

        try {
            if (msg) {
                await opsSendMessage({
                    conversationId: client?.conversationId!,
                    opsId: getUserId()!,
                    message: msg,
                });
            }
            if (files.length > 0) {
                await Promise.all(
                    newMsgContent.files.map(async (file) => {
                        const resFile = await opsSendMessage({
                            conversationId: client?.conversationId!,
                            opsId: getUserId()!,
                            fileId: file.id,
                        });
                        if (!resFile.error) {
                            return resFile.data.data;
                        }
                        return null;
                    }),
                );
            }
            message.success('Sent successfully!');
            setShowNewMsgModal(false);
            setNewMsgClientId(undefined);
            setNewMsgContent({ text: '', files: [] });
            setResetFlag({});
            resetAutoFetchInterval();
            refetchConversation(fetchConversationSize.current);
            refetchAllUnreadCount();
        } catch (error) {
            console.error('error:', error);
        } finally {
            setSendingNewMsg(false);
        }
    };

    const onClickReply = async () => {
        if (!targetClientDetail?.id) {
            run();
        }
        if (!replayContentValue.text.trim() && replayContentValue.files.length === 0) {
            return;
        }
        const messageStr = replayContentValue.text.trim() ? replayContentValue.text.replace(/<[^>]+>/g, '') : '';
        const files = replayContentValue.files.map((file) => file.id);

        try {
            if (messageStr) {
                const res = await opsSendMessage({
                    conversationId,
                    opsId: getUserId()!,
                    message: messageStr,
                });

                if (!res.error) {
                    setResetFlag({});
                    setClearMessageFlag({});

                    const messageData: TServerMessage = res.data?.data;
                    const cacheMessageList = conversationMessageMap.current[selectConversationId!] || [];
                    const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
                    conversationMessageMap.current[selectConversationId!] = _message || [];
                    setMessageList(_message || []);
                }
            }
            if (files.length > 0) {
                const res = await Promise.all(
                    replayContentValue.files.map(async (file) => {
                        const resFile = await opsSendMessage({
                            conversationId,
                            opsId: getUserId()!,
                            fileId: file.id,
                        });
                        if (!resFile.error) {
                            return resFile.data.data;
                        }
                        return null;
                    }),
                );
                res.forEach((newMsg) => {
                    if (newMsg) {
                        const cacheMessageList = conversationMessageMap.current[selectConversationId!] || [];
                        const _message = unitAndSortServerMessages([...(cacheMessageList || []), newMsg]);
                        conversationMessageMap.current[selectConversationId!] = _message || [];
                        setMessageList(_message || []);
                    }
                });
            }
            setReplayContentValue({ text: '', files: [] });
            scrollToLatestMessage();
            resetAutoFetchInterval();
            setTimeout(() => {
                refetchConversation(fetchConversationSize.current);
                refetchAllUnreadCount();
            }, 2000);
        } catch (error) {
            console.error('error:', error);
        } finally {
            setReplyingMsg(false);
        }
    };

    const onClickDiscard = async () => {
        setNewMsgClientId(undefined);
        setNewMsgContent({ text: '', files: [] });
        setResetFlag({});
    };

    const fetchMessageByConversationId = async (_cid: string, cursor?: string, size?: number) => {
        const param: TOpsGetConversationMessageListV2 = {
            conversationId: _cid,
            size: size || DEFAULT_FETCH_MESSAGE_COUNT,
        };
        if (cursor) {
            param.cursor = cursor;
        }
        setLoadingConversationMessage(true);
        const res = await opsGetConversationMessageList(param);
        if (!res.error) {
            const data = res?.data?.data?.items || [];
            const _isEmpty = data.length === 0;
            const _cursor = res?.data?.data?.cursor;
            const currentCursor = latestConversationMessageCursor.current[_cid] || '';
            if (!currentCursor || cursor) {
                //mean loading history or loading new but with any cursor
                latestConversationMessageCursor.current[_cid] = _cursor || '';
            }

            if (_isEmpty || !_cursor) {
                allConversationMessageFetchedFlag.current[_cid] = true;
            }

            // 合并缓存和新获取的消息
            // unitAndSortServerMessages 已经优化为：如果遇到相同的 sortKey，会保留最新的消息（基于 updatedAt）
            // 这样可以确保新消息（包含更新的回复内容）会覆盖旧缓存中的消息
            const cacheMessageList = conversationMessageMap.current[_cid] || [];
            const _message = unitAndSortServerMessages([...(cacheMessageList || []), ...data]);
            conversationMessageMap.current[_cid] = _message || [];
            if (_message && _message[_message.length - 1] && _message[_message.length - 1].aiReplyContent) {
                //last message
                setReplayContent(_message[_message.length - 1].aiReplyContent!);
            }
            setMessageList(_message || []);
            if (!cursor) {
                scrollToLatestMessage();
            }
        }
        setLoadingConversationMessage(false);
    };

    const loadHistoryMessage = async () => {
        if (loadingHistoryMessageRef.current === true) {
            return;
        }
        const targetConversationId = selectConversationIdRef.current;
        if (!targetConversationId) {
            return;
        }
        const cursor = latestConversationMessageCursor.current[targetConversationId] || '';
        if (!cursor) {
            return;
        }

        const allLoad = allConversationMessageFetchedFlag.current[targetConversationId];
        if (allLoad) {
            return;
        }
        const nowTime = new Date().getTime();
        const latestLoadTime = latestLoadHistoryTime.current;
        const diff = nowTime - latestLoadTime;
        if (diff < LOAD_HISTORY_INTERVAL) {
            return;
        }
        latestLoadHistoryTime.current = nowTime;
        loadingHistoryMessageRef.current = true;
        forceUpdate({});
        await fetchMessageByConversationId(targetConversationId, cursor, DEFAULT_FETCH_MESSAGE_COUNT);
        loadingHistoryMessageRef.current = false;
        forceUpdate({});
    };
    React.useEffect(() => {
        if (isEmpty) {
            return;
        }
        const messageScrollCheck = (e: Event) => {
            const target = e.target as HTMLElement;
            const { scrollTop } = target;
            if (scrollTop === 0) {
                loadHistoryMessage();
            }
        };

        const messageWrap = document.getElementById('messageWrap');
        if (messageWrap) {
            messageWrap.addEventListener('scroll', messageScrollCheck);
        }
        return () => {
            if (messageWrap) {
                messageWrap.removeEventListener('scroll', messageScrollCheck);
            }
        };
    }, [isEmpty]);

    React.useEffect(() => {
        return () => {
            if (socketRef) {
                socketRef.close();
            }
        };
    }, []);

    React.useEffect(() => {
        resetAutoFetchInterval();
        return () => {
            if (autoFetchConversationInterval.current) {
                clearInterval(autoFetchConversationInterval.current);
            }
        };
    }, []);

    React.useEffect(() => {
        if (conversation && conversation.length > 0) {
            if (currentTab === 'assigned_to_me') {
                const assignedToMeConversation = conversation?.filter((item) => {
                    const members = item.members || [];
                    return members.find((member) => {
                        return `${member.userId}` === `${getUserId()}` && member.userType === EMessageSenderType.OPS;
                    });
                });
                if (assignedToMeConversation && assignedToMeConversation.length > 0) {
                    if (!selectConversationIdRef.current) {
                        setSelectConversationId(assignedToMeConversation[0].conversationId);
                        selectConversationIdRef.current = assignedToMeConversation[0].conversationId;
                        setMessageListUnreadCount(assignedToMeConversation[0].unreadCount || 0);
                        return;
                    }
                }
                if (!assignedToMeConversation || assignedToMeConversation.length === 0) {
                    setSelectConversationId(undefined);
                    selectConversationIdRef.current = undefined;
                    setMessageListUnreadCount(0);
                    setMessageList([]);
                }
            } else if (!selectConversationIdRef.current) {
                setSelectConversationId(conversation[0].conversationId);
                selectConversationIdRef.current = conversation[0].conversationId;
                setMessageListUnreadCount(conversation[0].unreadCount || 0);
            }
        }
    }, [conversation, currentTab]);

    React.useEffect(() => {
        //for use in ws
        selectConversationIdRef.current = selectConversationId;
        if (selectConversationId) {
            fetchMessageByConversationId(selectConversationId);
        }
    }, [selectConversationId]);

    React.useEffect(() => {
        // 默认打开右侧 Client details；切换会话时也自动打开
        if (selectConversationId) {
            setShowClientInfo(true);
        }
    }, [selectConversationId]);

    React.useEffect(() => {
        if (clickSendMsgFlag) {
            handleShowMsgModal();
        }
    }, [clickSendMsgFlag]);

    return (
        <div className={`${s.wrap} ${isEmpty ? s.emptyWrap : ''}`}>
            <div className={s.left}>
                <div className={s.tabWrap}>
                    <div className={s.tab}>
                        <div
                            className={`${s.tabItem} ${currentTab === 'assigned_to_me' ? s.tabItemActive : ''}`}
                            onClick={() => setCurrentTab('assigned_to_me')}
                        >
                            Assigned to me
                            {!!allUnreadCount && <span className={s.tabItemCount}>{allUnreadCount || 0}</span>}
                        </div>
                        <div
                            className={`${s.tabItem} ${currentTab === 'all' ? s.tabItemActive : ''}`}
                            onClick={() => setCurrentTab('all')}
                        >
                            All
                        </div>
                    </div>
                </div>
                <div className={s.search}>
                    <div>
                        <Input
                            size="large"
                            placeholder="Search"
                            allowClear
                            className={s.searchInput}
                            value={conversationSearchValue}
                            onChange={(e) => setConversationSearchValue(e.target.value)}
                        />
                    </div>
                    {conversationSearchValue && (
                        <div className={s.searchResults}>{displayConversation?.length || 0} results</div>
                    )}
                </div>
                {!conversationSearchValue && (
                    <div
                        onClick={() => {
                            if (!hasUnreadMessageAll && !showUnreadConversationOnly) {
                                return;
                            }
                            setShowUnreadConversationOnly(!showUnreadConversationOnly);
                        }}
                        className={`${s.unReadLabel} ${
                            hasUnreadMessageAll ? s.unReadLabelEnable : ''
                        } ${showUnreadConversationOnly ? s.unReadLabelSelect : ''}`}
                    >
                        Unread ({allUnreadCount || 0})
                    </div>
                )}

                <div className={s.list}>
                    {displayConversation?.map((item) => {
                        const hasUnread = !!(item.unreadCount && item.unreadCount > 0);
                        const updateTime = dayjs(item.lastMessageAt);
                        const isToday = updateTime.isSame(dayjs(), 'day');
                        const isProviderReview =
                            item.conversationType === EConversationType.PROVIDER_REVIEW ||
                            item.conversationType === 'PROVIDER_REVIEW';
                        const itemInfo = getInfoFromConversation(item);
                        const members = item.members || [];
                        const isPatient =
                            members.some((member) => member.userType === EMessageSenderType.PATIENT) ||
                            members.some((member) => member.userType === EMessageSenderType.KLARITY_USER);
                        const isProvider = members.some((member) => member.userType === EMessageSenderType.PROVIDER);

                        // 对于 PROVIDER_REVIEW 类型，标题显示为 "Klarity reviews"
                        const displayTitle = isProviderReview
                            ? 'Klarity reviews'
                            : `${itemInfo.patientFirstName || ''} ${itemInfo.patientLastName || ''}`.trim();

                        let name = `${itemInfo.patientFirstName || ''} ${itemInfo.patientLastName || ''}`.trim();
                        if (item.lastMessageSenderType === EMessageSenderType.OPS) {
                            name = 'You';
                        } else if (item.lastMessageSenderType === EMessageSenderType.SYSTEM) {
                            name = 'System';
                        }
                        const hasAvatar = !!itemInfo.patientAvatarUrl && itemInfo.patientAvatarUrl !== '';
                        let labelText = '';
                        if (itemInfo && itemInfo.patientFirstName) {
                            labelText += itemInfo.patientFirstName.substring(0, 1);
                        }
                        if (itemInfo && itemInfo.patientLastName) {
                            labelText += itemInfo.patientLastName.substring(0, 1);
                        }
                        const isClosed = item.status === 'CLOSED';
                        const isNotMyConversation = !item.isOpsByMe;
                        const assignedOpsLabel = getAssignedOpsLabel(item);
                        const assignedOpsEmails = getAssignedOpsEmails(item);
                        const normalizeProviderType = (providerType?: string | null) => {
                            if (!providerType) return [];
                            const raw = String(providerType)
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean);
                            const types = raw.filter((t) => t === 'E' || t === 'K') as Array<'E' | 'K'>;
                            return Array.from(new Set(types));
                        };

                        const providerTypeLabels = normalizeProviderType(item.providerType).map((t) =>
                            t === 'E' ? 'PPV' : 'PPA',
                        );
                        return (
                            <div
                                className={`${s.item} ${hasUnread ? s.itemUnread : ''} ${
                                    selectConversationId === item.conversationId ? s.itemSelect : ''
                                } ${isClosed || isNotMyConversation ? s.itemClosed : ''}`}
                                key={item.conversationId}
                                onClick={() => {
                                    setClearMessageFlag({});
                                    setResetFlag({});
                                    handleChangeConversation(item.conversationId);
                                }}
                            >
                                <div className={s.itemHeader}>
                                    <div className={s.itemHeaderLeft}>
                                        {hasAvatar && <img src={itemInfo.patientAvatarUrl} className={s.hAvatar} />}
                                        {!hasAvatar && <div className={s.hAvatar}>{labelText}</div>}
                                        <span className={s.name}>
                                            {displayTitle}
                                            {itemInfo.patientOnline && (
                                                <span style={{ marginLeft: '6px' }} className={s.onlineIcon} />
                                            )}
                                        </span>
                                        {isPatient && <span className={s.patientLabel}>Patient</span>}
                                        {isProvider && <span className={s.providerLabel}>Provider</span>}
                                        {providerTypeLabels.map((label) => (
                                            <span key={label} className={s.providerTypeLabel}>
                                                {label}
                                            </span>
                                        ))}
                                    </div>
                                    <div className={s.itemHeaderRight}>
                                        {hasUnread && <span className={s.unReadCount}>{item.unreadCount}</span>}
                                    </div>
                                </div>
                                <div className={s.content}>
                                    {name}: {item.lastMessageContent}
                                </div>
                                {!!assignedOpsLabel && (
                                    <div className={s.assigneeRow}>
                                        <span className={s.assigneeKey}>Assigned:</span>
                                        {assignedOpsEmails.length > 0 ? (
                                            <Tooltip
                                                title={
                                                    <div>
                                                        {assignedOpsEmails.map((em) => (
                                                            <div key={em}>{em}</div>
                                                        ))}
                                                    </div>
                                                }
                                            >
                                                <span className={s.assigneeValue}>{assignedOpsLabel}</span>
                                            </Tooltip>
                                        ) : (
                                            <span className={s.assigneeValue}>{assignedOpsLabel}</span>
                                        )}
                                    </div>
                                )}
                                <div className={s.timeRow}>
                                    <ClockCircleOutlined
                                        style={{
                                            color: '#BDBDBD',
                                            marginRight: 4,
                                        }}
                                    />
                                    <span className={s.time}>
                                        {isToday ? 'Today, ' : ''}{' '}
                                        {getTimeStr(dayjs(item.lastMessageAt), 'MMM DD, hh:mm A')}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                    {hasMoreConversation && (
                        <div
                            className={s.viewMore}
                            onClick={() => {
                                fetchConversationSize.current = 200;
                            }}
                        >
                            <Button block>View more</Button>
                        </div>
                    )}
                </div>
            </div>
            <div className={s.mid}>
                {!isEmpty && conversation !== undefined && (
                    <>
                        <div
                            onClick={() => {
                                setShowClientInfo(!showClientInfo);
                            }}
                            className={s.cName}
                        >
                            <div className={s.cNameLeft}>
                                <span className={s.icon}>{conversationLabelText}</span>
                                {targetConversationInfo?.patientOnline && (
                                    <span
                                        style={{
                                            position: 'absolute',
                                            marginLeft: '24px',
                                            marginTop: '24px',
                                        }}
                                        className={s.onlineIcon}
                                    />
                                )}

                                {isTargetProviderReview
                                    ? 'Klarity reviews'
                                    : `${targetConversationInfo?.patientFirstName || ''} ${targetConversationInfo?.patientLastName || ''}`.trim()}
                                {(() => {
                                    const label = getAssignedOpsLabel(targetConversation);
                                    if (!label) return null;
                                    const emails = getAssignedOpsEmails(targetConversation);
                                    const node = <span className={s.cnAssignee}>Assigned: {label}</span>;
                                    if (!emails.length) return node;
                                    return (
                                        <Tooltip
                                            title={
                                                <div>
                                                    {emails.map((em) => (
                                                        <div key={em}>{em}</div>
                                                    ))}
                                                </div>
                                            }
                                        >
                                            {node}
                                        </Tooltip>
                                    );
                                })()}
                            </div>
                            <div className={s.cNameRight}>
                                <Popover
                                    title={null}
                                    content={
                                        <div className={s.popContent}>
                                            Transfer conversation to another ops team member
                                        </div>
                                    }
                                >
                                    <span
                                        className={`${s.cnIcon} ${canEdit ? '' : s.cnIconDisabled}`}
                                        onClick={(e) => {
                                            if (!canEdit) {
                                                return;
                                            }
                                            e.stopPropagation();
                                            setShowTransferChatConfirmModal(true);
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18"
                                            height="16"
                                            viewBox="0 0 18 16"
                                            fill="none"
                                        >
                                            <path
                                                d="M16.9989 9.99986C17.2538 10.0001 17.4989 10.0977 17.6843 10.2727C17.8696 10.4477 17.9811 10.6868 17.9961 10.9413C18.011 11.1957 17.9282 11.4462 17.7646 11.6417C17.601 11.8371 17.369 11.9628 17.1159 11.9929L16.9989 11.9999H3.41289L5.70589 14.2929C5.88524 14.4728 5.98937 14.7143 5.99712 14.9682C6.00488 15.2222 5.91568 15.4696 5.74764 15.6601C5.57961 15.8507 5.34534 15.9702 5.09242 15.9943C4.8395 16.0184 4.58688 15.9453 4.38589 15.7899L4.29189 15.7069L0.461892 11.8769C-0.203108 11.2129 0.222892 10.0939 1.12489 10.0059L1.23989 9.99986H16.9989ZM12.2919 0.292861C12.4641 0.120685 12.6932 0.0172534 12.9362 0.00197221C13.1792 -0.013309 13.4195 0.06061 13.6119 0.209861L13.7059 0.292861L17.5359 4.12286C18.2009 4.78686 17.7749 5.90586 16.8729 5.99386L16.7579 5.99986H0.998892C0.744012 5.99958 0.49886 5.90198 0.313524 5.72701C0.128188 5.55204 0.0166572 5.31291 0.00172004 5.05847C-0.0132171 4.80402 0.0695667 4.55348 0.233157 4.35803C0.396747 4.16258 0.628796 4.03696 0.881892 4.00686L0.998892 3.99986H14.5849L12.2919 1.70686C12.1044 1.51933 11.9991 1.26503 11.9991 0.999861C11.9991 0.734697 12.1044 0.480389 12.2919 0.292861Z"
                                                fill="#4B5563"
                                            />
                                        </svg>
                                    </span>
                                </Popover>
                                <Popover title={null} content={<div className={s.popContent}>Closed chat</div>}>
                                    <span
                                        className={`${s.cnIcon} ${canEdit ? '' : s.cnIconDisabled}`}
                                        onClick={(e) => {
                                            if (!canEdit) {
                                                return;
                                            }
                                            e.stopPropagation();
                                            setShowCloseChatConfirmModal(true);
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="21"
                                            height="21"
                                            viewBox="0 0 21 21"
                                            fill="none"
                                        >
                                            <path
                                                d="M16.5 1.5H1.5V18H10.5V19.5H0V0H18V9H16.5V1.5ZM6 6H14.9985V4.5H6V6ZM3 6V4.5H4.5V6H3ZM6 10.5V9H10.5V10.5H6ZM3 10.5V9H4.5V10.5H3ZM6 15V13.5H9V15H6ZM14.25 13.1895L12.129 11.0685L11.0685 12.129L13.1895 14.25L11.0685 16.371L12.129 17.4315L14.25 15.3105L16.371 17.4315L17.4315 16.371L15.3105 14.25L17.4315 12.129L16.371 11.0685L14.25 13.1895ZM3 15V13.5H4.5V15H3ZM14.25 7.5C15.1364 7.5 16.0142 7.67459 16.8331 8.01381C17.6521 8.35303 18.3962 8.85023 19.023 9.47703C19.6498 10.1038 20.147 10.8479 20.4862 11.6669C20.8254 12.4858 21 13.3636 21 14.25C21 15.1364 20.8254 16.0142 20.4862 16.8331C20.147 17.6521 19.6498 18.3962 19.023 19.023C18.3962 19.6498 17.6521 20.147 16.8331 20.4862C16.0142 20.8254 15.1364 21 14.25 21C12.4598 21 10.7429 20.2888 9.47703 19.023C8.21116 17.7571 7.5 16.0402 7.5 14.25C7.5 12.4598 8.21116 10.7429 9.47703 9.47703C10.7429 8.21116 12.4598 7.5 14.25 7.5Z"
                                                fill="#4B5563"
                                            />
                                        </svg>
                                    </span>
                                </Popover>
                            </div>
                        </div>

                        <div className={s.messageWrap} id="messageWrap">
                            {loadingHistoryMessageRef.current && <Spin className={s.messageLoadingHistory} />}
                            <OpsInboxMessageList
                                clientId={patientId}
                                inboxMessageList={displayMessage}
                                inboxConversation={targetConversation}
                                onReviewReplySuccess={() => {
                                    // Review 回复成功后，重新拉取当前会话的消息列表和会话列表
                                    resetAutoFetchInterval();
                                    refetchConversation(fetchConversationSize.current);
                                    refetchAllUnreadCount();
                                    const currentCid = selectConversationIdRef.current || conversationId;
                                    if (currentCid) {
                                        fetchMessageByConversationId(currentCid);
                                    }
                                }}
                                // billingPolicy={billingPolicy}
                            />
                            {loadingConversationMessage && !loadingHistoryMessageRef.current && (
                                <Spin className={s.messageLoading} />
                            )}
                        </div>
                        {isPPAProvider && (
                            <div className={s.reminderButtonsContainer}>
                                <IntakeFormReminderButton
                                    targetClient={targetClientDetail}
                                    patientId={patientId}
                                    selectConversationId={selectConversationId}
                                    conversationMessageMap={conversationMessageMap}
                                    setMessageList={setMessageList}
                                    scrollToLatestMessage={scrollToLatestMessage}
                                    resetAutoFetchInterval={resetAutoFetchInterval}
                                    refetchConversation={refetchConversation}
                                    refetchAllUnreadCount={refetchAllUnreadCount}
                                    fetchConversationSize={fetchConversationSize}
                                    urlClientId={urlClientId}
                                    run={run}
                                />
                                <AppointmentReminderButton
                                    targetClient={targetClientDetail}
                                    patientId={patientId}
                                    selectConversationId={selectConversationId}
                                    conversationMessageMap={conversationMessageMap}
                                    setMessageList={setMessageList}
                                    scrollToLatestMessage={scrollToLatestMessage}
                                    resetAutoFetchInterval={resetAutoFetchInterval}
                                    refetchConversation={refetchConversation}
                                    refetchAllUnreadCount={refetchAllUnreadCount}
                                    fetchConversationSize={fetchConversationSize}
                                    urlClientId={urlClientId}
                                    run={run}
                                />
                            </div>
                        )}

                        {!isTargetProviderReview && (
                            <div className={s.messageInput}>
                                {/* <div className={s.aiWrap}>
                                    <AiTextEditor
                                        rows={6}
                                        onChange={(e) => {
                                            if (typeof e === 'string') {
                                                setReplayContent(e as string);
                                            } else {
                                                setReplayContent(
                                                    e ? e.target.value : '',
                                                );
                                            }
                                        }}
                                        clearMessageFlag={clearMessageFlag}
                                        value={replayContent}
                                        type={EAIEditorType.PROSPECT}
                                        resetFlag={resetFlag}
                                        maxLength={3000}
                                        narrow={inputWidth < 750}
                                    />
                                </div>

                                <div className={s.btnWrap}>
                                    <Button
                                        className={s.btn}
                                        loading={replyingMsg}
                                        onClick={() => {
                                            onClickReply();
                                        }}
                                        type="primary"
                                        disabled={!replayContent}
                                    >
                                        Send
                                    </Button>
                                </div> */}
                                <ChatInput
                                    showNewTip
                                    value={replayContentValue}
                                    onChange={(value) => {
                                        console.log(value);
                                        setReplayContentValue(value);
                                    }}
                                    onSend={onClickReply}
                                    isOps
                                    placeholder="Enter your message"
                                    disabled={!canEdit}
                                    type={EAIEditorType.PROSPECT}
                                    clientId={targetClientDetail?.id || patientId || 0}
                                />
                            </div>
                        )}
                    </>
                )}
                {isEmpty && conversation !== undefined && (
                    <div className={s.emptyWrap}>
                        <img src={EmptyIcon} />
                        <div className={s.emptyTitle}>No messages yet</div>
                        <div className={s.emptyDes}>
                            Appointment updates, system notifications, and client messages will show up here. Start a
                            conversation with your clients anytime!
                        </div>
                        <Button onClick={handleShowMsgModal} type="primary">
                            Send message
                        </Button>
                    </div>
                )}
            </div>
            {showClientInfo && targetClientDetail && !isEmpty && (
                <div className={s.right}>
                    <div className={s.pTitle}>
                        <span>Client details</span>
                        <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                setShowClientInfo(false);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="25"
                                viewBox="0 0 24 25"
                                fill="none"
                            >
                                <path
                                    d="M6 18.5L18 6.5M6 6.5L18 18.5"
                                    stroke="#111827"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </div>
                    <div className={s.pWrap}>
                        <ProsepectInfo
                            // billingPolicy={billingPolicy}
                            forceResetInfo={forceResetInfo}
                            useForModule
                            smallSize
                            data={loadingTargetClientDetail ? undefined : targetClientDetail}
                            onUpdate={() => {
                                run();
                            }}
                            isOps
                        />
                    </div>
                </div>
            )}
            <div className={`${s.newMsgWrap} ${showNewMsgModal ? s.newMsgWrapShow : ''}`}>
                <div className={s.nTitle}>
                    <span>New message</span>
                    <svg
                        onClick={() => {
                            setNewMsgClientId(undefined);
                            setReplayContentValue({ text: '', files: [] });
                            setResetFlag({});
                            setShowNewMsgModal(false);
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M6 18L18 6M6 6L18 18"
                            stroke="#4B5563"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
                <div className={s.nContent}>
                    <div className={s.nLabel}>Client</div>
                    <Select
                        showSearch
                        className={s.select}
                        onChange={(val) => {
                            setNewMsgClientId(val);
                        }}
                        filterOption={(input, option) => {
                            const name = option?.children?.join('');
                            if (!name) return true;
                            return name?.toLowerCase().indexOf(input?.toLowerCase()) >= 0;
                        }}
                        value={newMsgClientId}
                        placeholder="Select a patient"
                        style={{ width: '200px' }}
                    >
                        {allClient?.map((item: TOpsClient) => {
                            return (
                                <Select.Option value={item.klarityUserId} key={item.klarityUserId}>
                                    {item.firstName} {item.lastName}
                                </Select.Option>
                            );
                        })}
                    </Select>
                    <div className={s.nLabel}>Message</div>
                    <div className={s.inputWrap}>
                        {/* <AiTextEditor
                            size="small"
                            rows={6}
                            maxLength={3000}
                            onChange={(e) => {
                                if (typeof e === 'string') {
                                    setNewMsgContent(e as string);
                                } else {
                                    setNewMsgContent(e ? e.target.value : '');
                                }
                            }}
                            value={newMsgContent}
                            type={EAIEditorType.PROSPECT}
                            resetFlag={resetFlag}
                            placeholder=""
                        /> */}
                        <ChatInput
                            value={newMsgContent}
                            onChange={(value) => {
                                setNewMsgContent(value);
                            }}
                            isOps
                            type={EAIEditorType.PROSPECT}
                            clientId={newMsgClientId!}
                            hideSend
                        />
                    </div>
                </div>
                <div className={s.nFooter}>
                    <Button style={{ marginRight: '16px' }} onClick={onClickDiscard}>
                        Discard
                    </Button>
                    <Button
                        loading={sendingNewMsg}
                        disabled={!newMsgClientId || (!newMsgContent.text.trim() && newMsgContent.files.length === 0)}
                        type="primary"
                        onClick={onClickSend}
                    >
                        Send
                    </Button>
                </div>
            </div>
            <TextVisitUnSubmittedDrawer
                show={showTextVisitUnSubmittedDrawer}
                onClose={() => setShowTextVisitUnSubmittedDrawer(false)}
                onNext={(data) => {
                    const { nextTextVisitUuid, inProgress } = data;
                    // console.log('onNext', nextTextVisitUuid, inProgress);
                    if (nextTextVisitUuid) {
                        setSelectedCase(undefined);
                        setNextCaseUuid(nextTextVisitUuid);
                        if (inProgress) {
                            setShowTextVisitUnSubmittedDrawer(true);
                        } else {
                            setShowPreviewDrawer(true);
                        }
                    }
                }}
                onSave={() => {}}
                textVisitUuid={selectedCase?.textVisitUuid || nextCaseUuid}
            />
            <Modal
                open={showCloseChatConfirmModal}
                onCancel={() => setShowCloseChatConfirmModal(false)}
                onOk={handleCloseChat}
                okText="Remove"
                title="Close chat"
                okButtonProps={{
                    danger: true,
                }}
            >
                <div className={s.closeChatContent}>
                    Once closed, the patient {targetConversationInfo.patientFirstName || ''}{' '}
                    {targetConversationInfo.patientLastName || ''} will no longer see this conversation in their portal
                    and will not be able to view or reply to it. This action cannot be undone.
                </div>
            </Modal>
            <Modal
                open={showTransferChatConfirmModal}
                onCancel={() => setShowTransferChatConfirmModal(false)}
                onOk={handleTransferChat}
                title="Transfer conversation"
                okText="Confirm transfer"
                okButtonProps={{
                    disabled: !transferChatTo,
                    loading: transferringChat,
                }}
            >
                <div className={s.transferChatContent}>
                    <div>
                        Select an ops team member to take over this conversation. The new assignee will be responsible
                        for all future replies.
                    </div>
                    <Select
                        onChange={(val) => {
                            setTransferChatTo(val);
                        }}
                        value={transferChatTo}
                        placeholder="Select an ops team member"
                        style={{ width: '100%', marginTop: '16px', marginBottom: '16px' }}
                        showSearch
                        filterOption={(input, option) => {
                            const name = option?.children?.join('');
                            if (!name) return true;
                            return name?.toLowerCase().indexOf(input?.toLowerCase()) >= 0;
                        }}
                    >
                        {opsUserList?.map((item) => {
                            return (
                                <Select.Option value={item.id} key={item.id}>
                                    {item.firstName} {item.lastName}
                                </Select.Option>
                            );
                        })}
                    </Select>
                    <div className={s.transferNote}>
                        <div className={s.transferNoteLabel}>
                            Note <span className={s.option}>(optional)</span>
                        </div>
                        <Input.TextArea
                            value={transferNote}
                            onChange={(e) => {
                                setTransferNote(e.target.value);
                            }}
                            rows={4}
                            maxLength={3000}
                            showCount
                            placeholder="Add context for the new assignee"
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default OpsMessageContainer;
